import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { MeetingRoutingRuleApiService } from '../../services/meeting-routing-rule-api.service';
import {
  MeetingRoutingRuleRequest,
  MeetingRoutingRuleResponse,
  RoutingRuleType,
} from '../../models/meeting.model';

@Component({
  selector: 'app-config-routing-rules-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './routing-rules-form.component.html',
})
export class RoutingRulesFormComponent implements OnInit {
  @Input() drawerMode = false;

  @Output() saved = new EventEmitter<MeetingRoutingRuleResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  isEditing = false;
  id: number | null = null;
  loading = false;
  saving = false;
  error = '';
  jsonError = '';

  readonly types: RoutingRuleType[] = [
    'contact_owner', 'territory', 'company_size', 'industry', 'least_busy', 'custom',
  ];

  constructor(
    private fb: FormBuilder,
    private api: MeetingRoutingRuleApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      ruleName: ['', [Validators.required, Validators.maxLength(200)]],
      ruleType: ['contact_owner', Validators.required],
      ruleConfig: ['{}', Validators.required],
      priorityOrder: [0],
      idBookingLink: [null],
      userTargetId: [null],
      idBookingLinkFallback: [null],
      isActive: [true],
    });
  }

  ngOnInit(): void {
    if (this.drawerMode) {
      // En drawer mode el wrapper invoca reset()/loadRule() al abrir.
      return;
    }
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.id = +id;
      this.load(this.id);
    }
  }

  reset(): void {
    this.isEditing = false;
    this.id = null;
    this.error = '';
    this.jsonError = '';
    this.saving = false;
    this.loading = false;
    this.form.reset({
      ruleName: '',
      ruleType: 'contact_owner',
      ruleConfig: '{}',
      priorityOrder: 0,
      idBookingLink: null,
      userTargetId: null,
      idBookingLinkFallback: null,
      isActive: true,
    });
  }

  loadRule(id: number): void {
    this.isEditing = true;
    this.id = id;
    this.load(id);
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar regla de routing' : 'Nueva regla de routing';
  }

  private load(id: number): void {
    this.loading = true;
    this.api.getById(id, this.businessId).subscribe({
      next: (r) => {
        this.form.patchValue(r);
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar';
        this.loading = false;
      },
    });
  }

  validateJson(): boolean {
    this.jsonError = '';
    const raw = this.form.get('ruleConfig')?.value;
    try {
      JSON.parse(raw);
      return true;
    } catch {
      this.jsonError = 'JSON inválido en ruleConfig';
      return false;
    }
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.validateJson()) return;

    this.saving = true;
    this.error = '';
    const v = this.form.value;
    const body: MeetingRoutingRuleRequest = {
      businessId: this.businessId,
      ruleName: v.ruleName.trim(),
      ruleType: v.ruleType,
      ruleConfig: v.ruleConfig,
      priorityOrder: v.priorityOrder ?? 0,
      idBookingLink: v.idBookingLink || undefined,
      userTargetId: v.userTargetId || undefined,
      idBookingLinkFallback: v.idBookingLinkFallback || undefined,
      isActive: v.isActive,
    };

    const op = this.isEditing && this.id
      ? this.api.update(this.id, this.businessId, body)
      : this.api.create(body);

    op.subscribe({
      next: (item) => {
        this.saving = false;
        if (this.drawerMode) {
          this.saved.emit(item);
        } else {
          this.router.navigate(['/meeting/config/routing-rules']);
        }
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
      },
    });
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/meeting/config/routing-rules']);
    }
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  /** Plantillas JSON útiles según ruleType */
  setTemplate(): void {
    const type: RoutingRuleType = this.form.get('ruleType')?.value;
    const templates: Record<RoutingRuleType, string> = {
      contact_owner: '{\n  "fallbackUserId": null\n}',
      territory: '{\n  "countries": ["PE", "CL"],\n  "defaultUserId": null\n}',
      company_size: '{\n  "minEmployees": 1,\n  "maxEmployees": 50,\n  "userId": null\n}',
      industry: '{\n  "industries": ["tech", "retail"],\n  "userId": null\n}',
      least_busy: '{\n  "pool": []\n}',
      custom: '{}',
    };
    this.form.patchValue({ ruleConfig: templates[type] || '{}' });
  }
}
